"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, Cloud, Loader2, X, ChevronDown } from "lucide-react";
import { AIRBRIDGE_PROJECTS } from "@/lib/airbridge-config";

type FetchStatus = "idle" | "requesting" | "polling" | "downloading" | "processing" | "error";

function getStepState(status: FetchStatus, step: 1 | 2 | 3): "pending" | "active" | "done" {
    const current = ({ requesting: 1, polling: 1, downloading: 2, processing: 3 } as Record<string, number>)[status] ?? 0;
    if (current > step) return "done";
    if (current === step) return "active";
    return "pending";
}

interface AirbridgeFetchV2Props {
    onDataLoaded: (csvText: string, displayName: string) => void;
    onFileSaved?: () => void;
    disabled?: boolean;
}

function getDefaultDates() {
    const now = new Date();
    const kstOffset = 9 * 60 * 60 * 1000;
    const kstNow = new Date(now.getTime() + kstOffset);
    const endDate = kstNow.toISOString().split("T")[0];
    const weekAgo = new Date(kstNow.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startDate = weekAgo.toISOString().split("T")[0];
    return { startDate, endDate };
}

export function AirbridgeFetchV2({ onDataLoaded, onFileSaved, disabled }: AirbridgeFetchV2Props) {
    const defaults = getDefaultDates();
    const [selectedProject, setSelectedProject] = useState("");
    const [startDate, setStartDate] = useState(defaults.startDate);
    const [endDate, setEndDate] = useState(defaults.endDate);
    const [status, setStatus] = useState<FetchStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const cancelRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const startTimer = () => {
        setElapsedSeconds(0);
        timerRef.current = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleCancel = () => {
        cancelRef.current = true;
        stopTimer();
        setStatus("idle");
    };

    const handleFetch = async () => {
        if (!selectedProject || !startDate || !endDate) return;

        cancelRef.current = false;
        setErrorMessage("");
        setStatus("requesting");
        startTimer();

        try {
            const postRes = await fetch("/api/airbridge/export", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ projectId: selectedProject, startDate, endDate }),
            });

            if (!postRes.ok) {
                const err = await postRes.json();
                throw new Error(err.error || "Export 요청 실패");
            }

            const { reportId } = await postRes.json();
            setStatus("polling");

            for (let i = 0; i < 150; i++) {
                if (cancelRef.current) return;
                await new Promise(r => setTimeout(r, i === 0 ? 3000 : 2000));
                if (cancelRef.current) return;

                const pollRes = await fetch(
                    `/api/airbridge/export?projectId=${selectedProject}&reportId=${reportId}`
                );

                if (!pollRes.ok) {
                    const err = await pollRes.json();
                    throw new Error(err.error || "상태 확인 실패");
                }

                const pollData = await pollRes.json();

                if (pollData.status === "completed") {
                    setStatus("downloading");

                    const dlRes = await fetch(
                        `/api/airbridge/export?downloadUrl=${encodeURIComponent(pollData.downloadUrl)}`
                    );

                    if (!dlRes.ok) {
                        const err = await dlRes.json();
                        throw new Error(err.error || "CSV 다운로드 실패");
                    }

                    const dlData = await dlRes.json();
                    stopTimer();

                    const project = AIRBRIDGE_PROJECTS.find(p => p.projectId === selectedProject);
                    const displayName = `${project?.displayName || selectedProject} (${startDate} ~ ${endDate})`;

                    // 데이터 관리에 자동 저장 (fire-and-forget)
                    const csvFileName = `${project?.appName || selectedProject}_${startDate}_${endDate}.csv`;
                    const formData = new FormData();
                    formData.append('file', new File([dlData.csvData], csvFileName, { type: 'text/csv' }));
                    fetch('/api/data-files', { method: 'POST', body: formData })
                        .then(() => onFileSaved?.())
                        .catch(() => {});

                    setStatus("processing");

                    await new Promise(r => setTimeout(r, 0));
                    if (cancelRef.current) return;

                    onDataLoaded(dlData.csvData, displayName);
                    setStatus("idle");
                    return;
                }
            }

            throw new Error("내보내기 시간 초과 (5분). 다시 시도해주세요.");
        } catch (error: unknown) {
            if (cancelRef.current) return;
            stopTimer();
            setStatus("error");
            setErrorMessage(error instanceof Error ? error.message : "알 수 없는 오류");
        }
    };

    const isActive = status !== "idle" && status !== "error";

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
                <Cloud className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-slate-300 font-medium">
                    Airbridge 데이터 가져오기
                </span>
            </div>

            <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700/50 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* 프로젝트 선택 */}
                    <div className="relative">
                        <select
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            disabled={isActive || disabled}
                            className="w-full appearance-none px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-600/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 disabled:opacity-50 pr-8"
                        >
                            <option value="">프로젝트 선택</option>
                            {AIRBRIDGE_PROJECTS.map((p) => (
                                <option key={p.projectId} value={p.projectId}>
                                    {p.displayName}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>

                    {/* 시작일 */}
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isActive || disabled}
                        className="px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-600/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 disabled:opacity-50"
                    />

                    {/* 종료일 */}
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isActive || disabled}
                        className="px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-600/50 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 disabled:opacity-50"
                    />
                </div>

                {/* 버튼 및 상태 */}
                <AnimatePresence mode="wait">
                    {isActive ? (
                        <motion.div
                            key="active"
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="space-y-1.5"
                        >
                            {([
                                { step: 1 as const, label: "Airbridge 추출 중", extra: (status === "requesting" || status === "polling") ? `${elapsedSeconds}초 경과` : undefined },
                                { step: 2 as const, label: "CSV 다운로드 중", extra: undefined },
                                { step: 3 as const, label: "대시보드 생성 중", extra: undefined },
                            ]).map(({ step, label, extra }) => {
                                const state = getStepState(status, step);
                                return (
                                    <div key={step} className="flex items-center gap-2 text-sm">
                                        {state === "done"
                                            ? <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                                            : state === "active"
                                            ? <Loader2 className="w-4 h-4 animate-spin text-violet-400 shrink-0" />
                                            : <Circle className="w-4 h-4 text-slate-600 shrink-0" />
                                        }
                                        <span className={state === "done" ? "text-slate-500" : state === "active" ? "text-slate-200 font-medium" : "text-slate-600"}>
                                            {label}
                                        </span>
                                        {extra && <span className="text-xs text-slate-400">({extra})</span>}
                                    </div>
                                );
                            })}
                            {status !== "processing" && (
                                <button
                                    onClick={handleCancel}
                                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors mt-1"
                                >
                                    <X className="w-3 h-3" />
                                    취소
                                </button>
                            )}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="idle"
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleFetch}
                                disabled={!selectedProject || !startDate || !endDate || disabled}
                                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-medium shadow-lg shadow-violet-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                            >
                                <Cloud className="w-4 h-4 inline mr-2" />
                                데이터 가져오기
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* 에러 */}
                {status === "error" && errorMessage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3"
                    >
                        {errorMessage}
                        <button
                            onClick={() => { setStatus("idle"); setErrorMessage(""); }}
                            className="ml-2 underline hover:text-red-300"
                        >
                            다시 시도
                        </button>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
